import { useCallback, useEffect, useRef } from "react";
import { API_BASE_URL } from "../services/api";

const SESSION_TIMEOUT = 10 * 60 * 1000;
const ENVIRONMENT =
  process.env.REACT_APP_ENV || process.env.NODE_ENV || "production";

const getStoredSession = () => {
  const raw = localStorage.getItem("collectorSession");
  return raw ? JSON.parse(raw) : null;
};

const storeSession = (session) => {
  localStorage.setItem("collectorSession", JSON.stringify(session));
};

const createSession = () => {
  const session = {
    session_id: crypto.randomUUID(),
    last_activity: Date.now(),
    ended: false,
  };
  storeSession(session);
  return session;
};

const send = (payload) => {
  const logsUrl = `https://analytics.omli.in/dashboard-events`;
  const token = localStorage.getItem("companioToken");

  // Add token to payload if available (since sendBeacon can't use headers)
  const dataWithAuth = token ? { ...payload, auth_token: token } : payload;
  const data = JSON.stringify(dataWithAuth);

  if (navigator.sendBeacon) {
    navigator.sendBeacon(logsUrl, data);
  } else {
    fetch(logsUrl, {
      method: "POST",
      mode: "no-cors",
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: data,
      keepalive: true,
    }).catch(() => {});
  }
};

export const useEventLogger = (user) => {
  const audioStartTimeRef = useRef(null);

  const ensureSession = useCallback(() => {
    const now = Date.now();
    let session = getStoredSession();

    if (!session || now - session.last_activity > SESSION_TIMEOUT) {
      if (session) {
        send({
          event_type: "SESSION_END",
          session_id: session.session_id,
          timestamp: session.last_activity,
          environment: ENVIRONMENT,
          dashboard: "stitch",
        });
      }

      session = createSession();
      send({
        event_type: "SESSION_START",
        session_id: session.session_id,
        timestamp: now,
        environment: ENVIRONMENT,
        dashboard: "stitch",
        user_email: user?.email,
      });
    } else {
      session.last_activity = now;
      storeSession(session);
    }

    return session;
  }, [user?.email]);

  const logEvent = useCallback(
    (eventType, metadata = {}) => {
      const session = ensureSession();
      session.last_activity = Date.now();
      storeSession(session);

      send({
        event_type: eventType,
        session_id: session.session_id,
        timestamp: Date.now(),
        environment: ENVIRONMENT,
        dashboard: "stitch",
        metadata,
      });
    },
    [ensureSession],
  );

  const logAudioPlay = useCallback(
    (taskId) => {
      audioStartTimeRef.current = Date.now();
      logEvent("AUDIO_PLAY", { task_id: taskId });
    },
    [logEvent],
  );

  const logTranscriptChange = useCallback(
    (taskId, originalLength, newLength) => {
      console.log("logTranscriptChange called:", {
        taskId,
        originalLength,
        newLength,
      });
      logEvent("TRANSCRIPT_CHANGE", {
        task_id: taskId,
        original_length: originalLength,
        new_length: newLength,
      });
    },
    [logEvent],
  );

  const logOptionSelect = useCallback(
    (taskId, optionType, value) => {
      logEvent("OPTION_SELECT", {
        task_id: taskId,
        option_type: optionType,
        value: value,
      });
    },
    [logEvent],
  );

  const logSubmit = useCallback(
    (taskId, timeFromAudioStart) => {
      logEvent("TASK_SUBMIT", {
        task_id: taskId,
        time_from_audio_start_ms: timeFromAudioStart,
      });
    },
    [logEvent],
  );

  const logSkip = useCallback(
    (taskId) => {
      logEvent("TASK_SKIP", { task_id: taskId });
    },
    [logEvent],
  );

  const logAudioSelect = useCallback(
    (taskId) => {
      logEvent("AUDIO_SELECT", { task_id: taskId });
    },
    [logEvent],
  );

  const logNavigation = useCallback(
    (page) => {
      logEvent("NAVIGATION", { page });
    },
    [logEvent],
  );

  const logLogout = useCallback(() => {
    logEvent("LOGOUT");
  }, [logEvent]);

  const logReset = useCallback(
    (taskId) => {
      logEvent("RESET_OPTIONAL_DETAILS", { task_id: taskId });
    },
    [logEvent],
  );

  const logRestore = useCallback(
    (taskId) => {
      logEvent("RESTORE_TRANSCRIPT", { task_id: taskId });
    },
    [logEvent],
  );

  useEffect(() => {
    ensureSession();
  }, [ensureSession]);

  return {
    logEvent,
    logAudioPlay,
    logTranscriptChange,
    logOptionSelect,
    logSubmit,
    logSkip,
    logAudioSelect,
    logNavigation,
    logLogout,
    logReset,
    logRestore,
  };
};
